/* Anomaly findings over the real API. No `X-Lumiq-Tenant` header — hard rule 1.
 *
 * **Evaluating is a POST with an `Idempotency-Key`, and the contrast with rulegen's PUT is the point.**
 * `ai-anomaly.yaml` states it: rulegen replaces a *derived set* and is idempotent on its natural key,
 * so a PUT was the honest verb. This **appends evidence of what was observed at a time**, so a retry
 * would append twice and re-deriving later against a moved baseline answers a different question. The
 * key is minted per dataset per mount, so a double-click is one evaluation and a deliberate second
 * evaluation is a second decision — `forgetIdempotencyKey` is what the screen calls between them.
 *
 * `Evaluation`, `StoredFinding` and `FindingDetail` are `allOf` compositions the emitter does not
 * handle, so they are composed here from the generated leaf guards — the shape the copilot slice
 * takes. A renamed field on either half still fails at the boundary rather than rendering blank.
 *
 * **Series state (mute / mark-expected / detector override) is keyed on `(dataset, signal)`**, not on
 * one finding: it is standing configuration for the monitored series, and a fresh finding every six
 * hours inherits it.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import {
  isAnomalyCapabilities,
  isBaselineBand,
  isDetectorInfo,
  isExplanation,
  isFinding,
  isSeriesPoint,
  isSeriesState,
} from '@/shared/api/generated/ai-anomaly';
import type {
  AnomalyCapabilities,
  BaselineBand,
  DetectorInfo,
  Explanation,
  Finding,
  SeriesPoint,
  SeriesState,
  SignalKind,
} from '@/shared/api/generated/ai-anomaly';
import {
  forgetIdempotencyKey,
  getOne,
  idempotencyKeyFor,
  post,
  put,
} from '@/shared/api/http';

export const CAPABILITIES_URL = '/v1/ai/anomaly/capabilities';
export const FINDINGS_URL = '/v1/ai/anomaly/findings';

export type {
  AnomalyCapabilities,
  BaselineBand,
  Basis,
  DetectorInfo,
  Explanation,
  ExplanationSource,
  ExplanationStatus,
  Finding,
  Irregularity,
  IrregularityCheck,
  Priority,
  SeriesPoint,
  SeriesState,
  SignalCapability,
  SignalKind,
  Verdict,
} from '@/shared/api/generated/ai-anomaly';

/** One signal's finding plus its explanation, as the evaluation route returns it. */
export interface Evaluation extends Finding {
  readonly explanation: Explanation;
  /** Null when nothing was opened — not actionable, or already on the queue. Dedup is healthy. */
  readonly incident_id: string | null;
  readonly finding_id: string | null;
}

/** A stored finding: the same fields plus its own id, the dataset it is about, when it was
 * evaluated, and whether its series is currently muted (so the list can hide muted series). */
export interface StoredFinding extends Finding {
  readonly id: string;
  readonly dataset_id: string;
  readonly evaluated_at: string;
  readonly muted: boolean;
  readonly explanation: Explanation;
}

/** Everything the detail screen draws around one finding.
 *
 * `detector` is `null` and `series` empty for a point-in-time finding: no detector decided it and no
 * series sits behind it, and the screen draws neither rather than a chart implying a comparison. */
export interface FindingDetail extends StoredFinding {
  readonly series: readonly SeriesPoint[];
  readonly baseline_band: BaselineBand | null;
  readonly detector: DetectorInfo | null;
  readonly series_state: SeriesState;
  readonly incident_id: string | null;
}

export interface EvaluationOutcome {
  readonly dataset_id: string;
  readonly evaluations: readonly Evaluation[];
  readonly verdict_counts: Readonly<Record<string, number>>;
}

function hasExplanation(value: unknown): boolean {
  if (typeof value !== 'object' || value === null) return false;
  return isExplanation((value as Record<string, unknown>)['explanation']);
}

function isEvaluation(value: unknown): value is Evaluation {
  return isFinding(value) && hasExplanation(value);
}

function isStoredFinding(value: unknown): value is StoredFinding {
  if (!isFinding(value) || !hasExplanation(value)) return false;
  const candidate = value as unknown as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['dataset_id'] === 'string' &&
    typeof candidate['evaluated_at'] === 'string' &&
    typeof candidate['muted'] === 'boolean'
  );
}

function isFindingDetail(value: unknown): value is FindingDetail {
  if (!isStoredFinding(value)) return false;
  const candidate = value as unknown as Record<string, unknown>;
  const series = candidate['series'];
  const band = candidate['baseline_band'];
  return (
    Array.isArray(series) &&
    series.every(isSeriesPoint) &&
    (band === null || isBaselineBand(band)) &&
    (candidate['detector'] === null || isDetectorInfo(candidate['detector'])) &&
    isSeriesState(candidate['series_state']) &&
    (candidate['incident_id'] === null || typeof candidate['incident_id'] === 'string')
  );
}

function isEvaluationOutcome(value: unknown): value is EvaluationOutcome {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['dataset_id'] === 'string' &&
    Array.isArray(candidate['evaluations']) &&
    candidate['evaluations'].every(isEvaluation) &&
    candidate['verdict_counts'] !== undefined
  );
}

function isFindingList(
  value: unknown,
): value is { readonly findings: readonly StoredFinding[] } {
  if (typeof value !== 'object' || value === null) return false;
  const findings = (value as Record<string, unknown>)['findings'];
  return Array.isArray(findings) && findings.every(isStoredFinding);
}

function isRaisedIncident(
  value: unknown,
): value is { readonly incident_id: string | null } {
  if (typeof value !== 'object' || value === null) return false;
  const id = (value as Record<string, unknown>)['incident_id'];
  return id === null || typeof id === 'string';
}

export function useAnomalyCapabilities(): UseQueryResult<AnomalyCapabilities> {
  return useQuery({
    queryKey: ['anomaly', 'capabilities'],
    queryFn: () =>
      getOne(CAPABILITIES_URL, isAnomalyCapabilities, 'anomaly capabilities'),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export const FINDINGS_PAGE_SIZE = 50;
/** What `GET /findings` returns at most, and it has no cursor.

`AnomalyService.recent` defaults to `limit=50` and the router passes no query parameter, so a client
cannot page or raise it. The list is **current state** — the latest finding per series and the
latest profile run per dataset — ordered by urgency *before* the bound, so a full page is "the 50
most urgent", never a cut that dropped a critical row. §9 carries the cursor itself as backlog.
*/

export function useFindings(): UseQueryResult<readonly StoredFinding[]> {
  return useQuery({
    queryKey: ['anomaly', 'findings'],
    queryFn: () => getOne(FINDINGS_URL, isFindingList, 'anomaly findings'),
    select: (body) => body.findings,
    retry: false,
  });
}

export function useFinding(findingId: string): UseQueryResult<FindingDetail> {
  return useQuery({
    queryKey: ['anomaly', 'finding', findingId],
    queryFn: () =>
      getOne(
        `${FINDINGS_URL}/${encodeURIComponent(findingId)}`,
        isFindingDetail,
        'anomaly finding',
      ),
    enabled: findingId.length > 0,
    retry: false,
  });
}

export function useEvaluateDataset(): UseMutationResult<
  EvaluationOutcome,
  Error,
  string
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (datasetId: string) =>
      post(
        `/v1/ai/anomaly/datasets/${encodeURIComponent(datasetId)}/evaluations`,
        idempotencyKeyFor('anomaly-evaluate', datasetId),
        isEvaluationOutcome,
        'evaluation outcome',
        {},
      ),
    onSuccess: (outcome) => {
      /* The key is forgotten *after* a success, so the next deliberate evaluation of the same dataset
         is a new decision rather than a replay of this one. */
      forgetIdempotencyKey('anomaly-evaluate', outcome.dataset_id);
      void client.invalidateQueries({ queryKey: ['anomaly', 'findings'] });
      void client.invalidateQueries({ queryKey: ['anomaly', 'finding'] });
    },
    retry: false,
  });
}

/** The `(dataset, signal)` pair a series-state change is about. */
export interface SeriesRef {
  readonly datasetId: string;
  readonly signal: SignalKind;
}

function seriesUrl(ref: SeriesRef, action: string): string {
  return (
    `/v1/ai/anomaly/datasets/${encodeURIComponent(ref.datasetId)}` +
    `/signals/${encodeURIComponent(ref.signal)}/${action}`
  );
}

function seriesScope(ref: SeriesRef): string {
  return `${ref.datasetId}:${ref.signal}`;
}

function useSeriesStateMutation(
  action: string,
  scope: string,
  buildBody: (input: SeriesRef & { readonly text?: string }) => unknown,
): UseMutationResult<SeriesState, Error, SeriesRef & { readonly text?: string }> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      post(
        seriesUrl(input, action),
        idempotencyKeyFor(scope, seriesScope(input)),
        isSeriesState,
        'series state',
        buildBody(input),
      ),
    onSuccess: (_state, input) => {
      /* Every toggle is its own decision — muting then unmuting an hour later is two, not a
         replay — so the key is forgotten after each success. */
      forgetIdempotencyKey(scope, seriesScope(input));
      void client.invalidateQueries({ queryKey: ['anomaly', 'findings'] });
      void client.invalidateQueries({ queryKey: ['anomaly', 'finding'] });
    },
    retry: false,
  });
}

export function useMuteSeries(): UseMutationResult<
  SeriesState,
  Error,
  SeriesRef & { readonly text?: string }
> {
  return useSeriesStateMutation('mute', 'anomaly-mute', (input) => ({
    reason: input.text ?? '',
  }));
}

export function useUnmuteSeries(): UseMutationResult<SeriesState, Error, SeriesRef> {
  return useSeriesStateMutation('unmute', 'anomaly-unmute', () => ({}));
}

export function useMarkExpected(): UseMutationResult<
  SeriesState,
  Error,
  SeriesRef & { readonly text?: string }
> {
  return useSeriesStateMutation('mark-expected', 'anomaly-expected', (input) => ({
    note: input.text ?? '',
  }));
}

export function useClearExpected(): UseMutationResult<SeriesState, Error, SeriesRef> {
  return useSeriesStateMutation('clear-expected', 'anomaly-clear-expected', () => ({}));
}

export function useSetThreshold(): UseMutationResult<
  SeriesState,
  Error,
  SeriesRef & { readonly override: number | null }
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      put(seriesUrl(input, 'detector'), isSeriesState, 'series state', {
        threshold_override: input.override,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['anomaly', 'findings'] });
      void client.invalidateQueries({ queryKey: ['anomaly', 'finding'] });
    },
    retry: false,
  });
}

export function useRaiseIncident(): UseMutationResult<
  { readonly incident_id: string | null },
  Error,
  string
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (findingId: string) =>
      post(
        `${FINDINGS_URL}/${encodeURIComponent(findingId)}/incident`,
        /* Not forgotten on success, unlike the toggles: one incident per condition. A second press
           should replay the first, not open a second incident for the same finding. */
        idempotencyKeyFor('anomaly-incident', findingId),
        isRaisedIncident,
        'raised incident',
        {},
      ),
    onSuccess: (_result, findingId) => {
      void client.invalidateQueries({ queryKey: ['anomaly', 'findings'] });
      void client.invalidateQueries({ queryKey: ['anomaly', 'finding', findingId] });
    },
    retry: false,
  });
}
